import { Link } from 'react-router-dom';

export default function SEOContent() {
  return (
    <>
      {/* What Is Sugargoo Spreadsheet Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
              What Is Sugargoo Spreadsheet?
            </h2>

            <div className="space-y-4 text-foreground-700 leading-relaxed text-base">
              <p>
                <strong>Sugargoo Spreadsheet</strong> is an organized resource system that helps shoppers discover products, compare prices, check categories, and improve their China shopping experience. Whether you are a seasoned buyer or just getting started with international shopping, having a structured approach makes all the difference.
              </p>
              <p>
                Unlike random product links scattered across forums and social media, a well-maintained Sugargoo Spreadsheet saves you time by organizing everything in one accessible place. You get curated product categories, direct links, price comparisons, and quality control resources — all designed to streamline your shopping workflow.
              </p>
              <p>
                The beauty of using a <strong>Sugargoo Spreadsheet</strong> lies in its simplicity. Instead of spending hours searching through endless listings, you can browse organized categories — from shoes and hoodies to accessories and bags — each with verified product links that lead directly to trusted sources. This approach transforms what could be an overwhelming experience into a smooth, efficient shopping journey.
              </p>
            </div>

            <div className="mt-10 overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-background-300">
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Method</th>
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Search Speed</th>
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Organization</th>
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Beginner Friendly</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200">
                    <td className="py-3 px-4 text-foreground-700">Random Searching</td>
                    <td className="py-3 px-4 text-accent-600">Slow</td>
                    <td className="py-3 px-4 text-accent-600">Poor</td>
                    <td className="py-3 px-4 text-accent-600">No</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 px-4 text-foreground-700">Social Media Links</td>
                    <td className="py-3 px-4 text-secondary-600">Medium</td>
                    <td className="py-3 px-4 text-secondary-600">Low</td>
                    <td className="py-3 px-4 text-secondary-600">Medium</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-foreground-900 font-semibold">Sugargoo Spreadsheet</td>
                    <td className="py-3 px-4 text-accent-600 font-semibold">Fast</td>
                    <td className="py-3 px-4 text-accent-600 font-semibold">Excellent</td>
                    <td className="py-3 px-4 text-accent-600 font-semibold">Yes</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Why Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">
              Why Thousands of Users Search Sugargoo Spreadsheet
            </h2>
            <p className="text-foreground-600 text-base max-w-xl mx-auto">
              Join a growing community of savvy shoppers who rely on organized resources for their Sugargoo shopping experience.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl mx-auto">
            {[
              { number: '10,000+', label: 'Organized Resources' },
              { number: '100+', label: 'Shopping Guides' },
              { number: '50+', label: 'Comparison Articles' },
              { number: 'Monthly', label: 'Updated Content' },
            ].map((stat) => (
              <div key={stat.label} className="text-center p-6 bg-background-100 rounded-xl border border-background-200/60">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-2">
                  {stat.number}
                </div>
                <div className="text-sm text-foreground-600">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Review Section */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
              Sugargoo Review: Is Sugargoo Legit?
            </h2>
            <div className="flex items-center gap-1 mb-4 text-xl text-amber-400">
              <i className="ri-star-fill"></i>
              <i className="ri-star-fill"></i>
              <i className="ri-star-fill"></i>
              <i className="ri-star-fill"></i>
              <i className="ri-star-line"></i>
              <span className="text-sm text-foreground-500 ml-2">4.0 / 5.0</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-6">
              <div className="bg-background-50 p-6 rounded-xl border border-background-200/60">
                <h3 className="text-base font-semibold text-accent-600 mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-checkbox-circle-fill"></i></span>
                  Pros
                </h3>
                <ul className="space-y-2 text-sm text-foreground-700">
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500"><i className="ri-check-line"></i></span>
                    Large warehouse system with global reach
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500"><i className="ri-check-line"></i></span>
                    Product sourcing support across categories
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500"><i className="ri-check-line"></i></span>
                    Multiple international shipping options
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-accent-500"><i className="ri-check-line"></i></span>
                    Quality control photo service
                  </li>
                </ul>
              </div>
              <div className="bg-background-50 p-6 rounded-xl border border-background-200/60">
                <h3 className="text-base font-semibold text-secondary-600 mb-3 flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center"><i className="ri-information-fill"></i></span>
                  Cons
                </h3>
                <ul className="space-y-2 text-sm text-foreground-700">
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-secondary-500"><i className="ri-close-line"></i></span>
                    Shipping cost varies by destination and weight
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="w-4 h-4 flex items-center justify-center mt-0.5 text-secondary-500"><i className="ri-close-line"></i></span>
                    Learning curve for complete beginners
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/sugargoo-review-is-sugargoo-legit"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200"
              >
                Read Full Sugargoo Review
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Shipping Section */}
      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-6">
              Sugargoo Shipping Cost Guide
            </h2>
            <p className="text-foreground-700 leading-relaxed text-base mb-8">
              Understanding Sugargoo shipping costs is essential for getting the best value. Shipping fees depend on several factors including package weight, destination country, shipping method, and optional insurance coverage. By comparing different shipping options, you can find the balance between speed and cost that works for your budget.
            </p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b-2 border-background-300">
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Shipping Method</th>
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Speed</th>
                    <th className="text-left py-3 px-4 text-foreground-800 font-semibold">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-background-200">
                    <td className="py-3 px-4 text-foreground-700 font-medium">EMS</td>
                    <td className="py-3 px-4 text-accent-600">Fast</td>
                    <td className="py-3 px-4 text-foreground-700">Medium</td>
                  </tr>
                  <tr className="border-b border-background-200">
                    <td className="py-3 px-4 text-foreground-700 font-medium">Air Freight</td>
                    <td className="py-3 px-4 text-secondary-600">Medium</td>
                    <td className="py-3 px-4 text-foreground-700">Low</td>
                  </tr>
                  <tr>
                    <td className="py-3 px-4 text-foreground-700 font-medium">Sea Freight</td>
                    <td className="py-3 px-4 text-foreground-700">Slow</td>
                    <td className="py-3 px-4 text-accent-600 font-semibold">Cheapest</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/sugargoo-shipping-cost-guide"
                className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors duration-200"
              >
                View Full Shipping Cost Guide
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line"></i></span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-12 md:py-16 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-900 mb-6">
              Sugargoo Spreadsheet Safety Information
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                'Does not process payments directly',
                'Does not store financial information',
                'Does not sell products directly',
                'Provides educational resources only',
                'Uses HTTPS encryption',
                'Respects user privacy',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3 p-4 bg-background-50 rounded-lg border border-background-200/60">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-500 flex-shrink-0">
                    <i className="ri-shield-check-line"></i>
                  </span>
                  <span className="text-sm text-foreground-700">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}